import { useCallback, useEffect, useMemo, useState } from "react";

import api from "../../../utils/api/axiosInstance";

const EMPTY_REVIEW_FORM = {
  rating: 0,
  comment: "",
  outcomeRatings: {},
  ownershipDuration: "",
  variant: "",
  mainUse: "",
  comparedToPrior: "",
};

const useProductReviews = ({
  productId,
  user,
  setUser,
  showToast,
  navigate,
  location,
  trackReview,
  onReviewsChanged,
}) => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState(EMPTY_REVIEW_FORM);
  const [reviewFormConfig, setReviewFormConfig] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewForm, setEditReviewForm] = useState({
    rating: 0,
    comment: "",
    outcomeRatings: {},
    ownershipDuration: "",
    variant: "",
    mainUse: "",
    comparedToPrior: "",
  });
  const [showDeleteReviewModal, setShowDeleteReviewModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  const reloadReviews = useCallback(async () => {
    if (!productId) return;
    try {
      const reviewsRes = await api.get(`/products/${productId}/reviews`);
      if (reviewsRes.data && reviewsRes.data.reviews && reviewsRes.data.reviews.length > 0) {
        setReviews(reviewsRes.data.reviews);
        setAverageRating(reviewsRes.data.averageRating || 0);
        setTotalReviews(reviewsRes.data.totalReviews || 0);
      } else {
        setReviews([]);
        setAverageRating(0);
        setTotalReviews(0);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviews([]);
      setAverageRating(0);
    }
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    reloadReviews();
  }, [productId, reloadReviews]);

  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/products/${productId}/review-form`);
        if (!cancelled) setReviewFormConfig(res?.data || null);
      } catch {
        if (!cancelled) setReviewFormConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const isUserReview = useCallback(
    (review) => {
      if (!user || !review.user) return false;
      const userId = user.id || user._id;
      const reviewUserId = review.user._id || review.user;
      return (
        String(reviewUserId) === String(userId) ||
        (review.user.email && user.email && review.user.email === user.email)
      );
    },
    [user],
  );

  const handleHelpfulInReviewsModal = useCallback(async (reviewId) => {
    try {
      const helpfulRes = await api.post(`/products/reviews/${reviewId}/helpful`);
      setReviews((prev) =>
        prev.map((r) =>
          r._id === reviewId ? { ...r, helpful: helpfulRes.data.helpful } : r,
        ),
      );
    } catch (err) {
      console.error("Error marking helpful:", err);
    }
  }, []);

  const handleReviewHelpful = useCallback(
    async (reviewId) => {
      if (!user) {
        navigate("/login", {
          state: { from: location, action: "mark review helpful" },
        });
        return;
      }
      await handleHelpfulInReviewsModal(reviewId);
    },
    [user, navigate, location, handleHelpfulInReviewsModal],
  );

  const handleSubmitReview = useCallback(async () => {
    if (!reviewForm.rating || !reviewForm.comment.trim()) {
      showToast({
        message: "Please provide both a rating and comment",
        type: "error",
      });
      return;
    }
    if (!productId) return;

    try {
      const submitRes = await api.post(`/products/${productId}/reviews`, {
        rating: reviewForm.rating,
        comment: reviewForm.comment,
        outcomeRatings: Object.entries(reviewForm.outcomeRatings || {})
          .filter(([, value]) => value > 0)
          .map(([key, value]) => ({ key, rating: value })),
        ownershipDuration: reviewForm.ownershipDuration || null,
        variant: reviewForm.variant || null,
        mainUse: reviewForm.mainUse || null,
        comparedToPrior: reviewForm.comparedToPrior || null,
      });

      trackReview(productId, reviewForm.rating);

      const pointsPayload = submitRes.data?.points;
      if (pointsPayload && setUser) {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                points: pointsPayload.totalPoints ?? prev.points,
                tier: pointsPayload.newTier ?? prev.tier,
              }
            : prev,
        );
        if (pointsPayload.points > 0) {
          showToast({
            message: `+${pointsPayload.points} points`,
            type: "success",
            duration: 2000,
          });
        }
      }

      showToast({
        message: submitRes.data?.message || "Review submitted and is pending approval",
        type: "success",
      });
      if (submitRes.data?.badges?.length) {
        submitRes.data.badges.forEach((badge) => {
          showToast({ message: `Badge unlocked: ${badge.name}`, type: "success" });
        });
      }

      setReviewForm(EMPTY_REVIEW_FORM);
      setShowReviewModal(false);
      await reloadReviews();
      await onReviewsChanged?.(productId);
    } catch (err) {
      console.error("Failed to submit review:", err);
      showToast({
        message: err.response?.data?.message || "Failed to submit review",
        type: "error",
      });
    }
  }, [
    reviewForm,
    showToast,
    productId,
    trackReview,
    setUser,
    reloadReviews,
    onReviewsChanged,
  ]);

  const handleEditReview = useCallback(
    async (reviewId) => {
      if (!editReviewForm.rating || !editReviewForm.comment.trim()) {
        showToast({
          message: "Please provide both a rating and comment",
          type: "error",
        });
        return;
      }
      if (!productId) return;

      try {
        const updateRes = await api.put(`/products/reviews/${reviewId}`, {
          rating: editReviewForm.rating,
          comment: editReviewForm.comment,
          outcomeRatings: Object.entries(editReviewForm.outcomeRatings || {})
            .filter(([, value]) => value > 0)
            .map(([key, value]) => ({ key, rating: value })),
          ownershipDuration: editReviewForm.ownershipDuration || null,
          variant: editReviewForm.variant || null,
          mainUse: editReviewForm.mainUse || null,
          comparedToPrior: editReviewForm.comparedToPrior || null,
        });

        const pointsPayload = updateRes.data?.points;
        if (pointsPayload?.points > 0) {
          showToast({
            message: `+${pointsPayload.points} points`,
            type: "success",
            duration: 2000,
          });
        }
        showToast({
          message: updateRes.data?.message || "Review updated",
          type: "success",
        });

        setEditingReview(null);
        setEditReviewForm({
      rating: 0,
      comment: "",
      outcomeRatings: {},
      ownershipDuration: "",
      variant: "",
      mainUse: "",
      comparedToPrior: "",
    });
        await reloadReviews();
        await onReviewsChanged?.(productId);
      } catch (err) {
        console.error("Failed to update review:", err);
        showToast({
          message: err.response?.data?.message || "Failed to update review",
          type: "error",
        });
      }
    },
    [editReviewForm, showToast, productId, reloadReviews, onReviewsChanged],
  );

  const startEditReview = useCallback((review) => {
    setEditingReview(review._id);
    setEditReviewForm({
      rating: review.rating,
      comment: review.comment,
      outcomeRatings: Object.fromEntries(
        (review.outcomeRatings || []).map((item) => [item.key, item.rating]),
      ),
      ownershipDuration: review.ownershipDuration || "",
      variant: review.variant || "",
      mainUse: review.mainUse || "",
      comparedToPrior: review.comparedToPrior || "",
    });
  }, []);

  const cancelEditReview = useCallback(() => {
    setEditingReview(null);
    setEditReviewForm({
      rating: 0,
      comment: "",
      outcomeRatings: {},
      ownershipDuration: "",
      variant: "",
      mainUse: "",
      comparedToPrior: "",
    });
  }, []);

  const requestDeleteReview = useCallback((reviewId) => {
    setReviewToDelete(reviewId);
    setShowDeleteReviewModal(true);
  }, []);

  const cancelDeleteReview = useCallback(() => {
    setShowDeleteReviewModal(false);
    setReviewToDelete(null);
  }, []);

  const confirmDeleteReview = useCallback(async () => {
    if (!reviewToDelete || !productId) return;

    try {
      await api.delete(`/products/reviews/${reviewToDelete}`);
      showToast({ message: "Review deleted successfully!", type: "success" });
      await reloadReviews();
      await onReviewsChanged?.(productId);
    } catch (err) {
      console.error("Failed to delete review:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete review",
        type: "error",
      });
    } finally {
      setShowDeleteReviewModal(false);
      setReviewToDelete(null);
    }
  }, [reviewToDelete, productId, showToast, reloadReviews, onReviewsChanged]);

  const ratingDistribution = useMemo(() => {
    const approved = reviews.filter((review) => review.approved);
    if (!approved.length) return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    approved.forEach((review) => {
      if (review.rating >= 1 && review.rating <= 5) {
        dist[review.rating]++;
      }
    });
    return dist;
  }, [reviews]);

  return {
    reviews,
    averageRating,
    totalReviews,
    ratingDistribution,
    showReviewModal,
    setShowReviewModal,
    reviewForm,
    setReviewForm,
    reviewFormConfig,
    handleSubmitReview,
    editingReview,
    setEditingReview,
    editReviewForm,
    setEditReviewForm,
    handleEditReview,
    startEditReview,
    cancelEditReview,
    isUserReview,
    handleHelpfulInReviewsModal,
    handleReviewHelpful,
    showDeleteReviewModal,
    requestDeleteReview,
    cancelDeleteReview,
    confirmDeleteReview,
    reloadReviews,
  };
};

export default useProductReviews;
